/**
 * Centralized constants for the application
 * Reduces magic strings and improves maintainability
 */

// ============= Edge Functions =============
export const EDGE_FUNCTIONS = {
  STEEL_CHAT: 'steel-chat',
  PROCESS_DOCUMENT: 'process-document',
  BATCH_PROCESS_DOCUMENTS: 'batch-process-documents',
  GENERATE_EMBEDDINGS: 'generate-embeddings',
  SMART_FEATURES: 'smart-features',
  VOICE_TO_TEXT: 'voice-to-text',
  HELP_CHAT: 'help-chat',
} as const;

// ============= Database Tables =============
export const DB_TABLES = {
  PROFILES: 'profiles',
  USER_ROLES: 'user_roles',
  PERMANENT_DOCUMENTS: 'permanent_documents',
  DOCUMENT_CHUNKS: 'document_chunks',
  DOCUMENT_CATEGORIES: 'document_categories',
  DOCUMENT_FOLDERS: 'document_folders',
  DOCUMENT_TAGS: 'document_tags',
  TAGS: 'tags',
  CONVERSATIONS: 'conversations',
  CHAT_MESSAGES: 'chat_messages',
  CONVERSATION_SHARES: 'conversation_shares',
  CONVERSATION_COMMENTS: 'conversation_comments',
  TEAMS: 'teams',
  TEAM_MEMBERS: 'team_members',
  TEAM_DOCUMENT_ACCESS: 'team_document_access',
  GLOSSARY_TERMS: 'glossary_terms',
  HELP_CONTENT: 'help_content',
  USER_FEEDBACK: 'user_feedback',
  AUDIT_LOGS: 'audit_logs',
  RATE_LIMIT_EVENTS: 'rate_limit_events',
} as const;

// ============= Storage Buckets =============
export const STORAGE_BUCKETS = {
  PERMANENT_DOCUMENTS: 'permanent-documents',
  AVATARS: 'avatars',
  DOCUMENT_IMAGES: 'document-images',
} as const;

// ============= LocalStorage Keys =============
export const LOCAL_STORAGE_KEYS = {
  PWA_INSTALL_DISMISSED: 'pwa-install-dismissed',
  NOTIFICATION_PROMPT_DISMISSED: 'notification-prompt-dismissed',
  THEME: 'theme',
} as const;

// ============= LocalForage Stores =============
export const LOCALFORAGE_CONFIG = {
  DB_NAME: 'pm-assistant',
  STORES: {
    CONVERSATIONS: 'conversations',
    DOCUMENTS: 'documents',
    METADATA: 'metadata',
  },
} as const;

// ============= API Endpoints =============
export const API_ENDPOINTS = {
  SUPABASE_FUNCTIONS_BASE: '/functions/v1',
} as const;

// ============= Document Status =============
export const DOCUMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  ERROR: 'error',
} as const;

// ============= Embedding Status =============
export const EMBEDDING_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  ERROR: 'error',
} as const;

// ============= User Roles =============
export const USER_ROLES = {
  ADMIN: 'admin',
  SITE_ADMIN: 'site_admin',
  DOCUMENT_UPLOADER: 'document_uploader',
  USER: 'user',
} as const;

// ============= Team Member Roles =============
export const TEAM_ROLES = {
  ADMIN: 'admin',
  MEMBER: 'member',
} as const;

// ============= Share Types =============
export const SHARE_TYPES = {
  USER: 'user',
  TEAM: 'team',
} as const;

// ============= Message Roles =============
export const MESSAGE_ROLES = {
  USER: 'user',
  ASSISTANT: 'assistant',
  SYSTEM: 'system',
} as const;

// ============= File Types =============
export const FILE_TYPES = {
  PDF: 'pdf',
  DOC: 'doc',
  DOCX: 'docx',
  TXT: 'txt',
  CSV: 'csv',
  XLS: 'xls',
  XLSX: 'xlsx',
  PNG: 'png',
  JPG: 'jpg',
  JPEG: 'jpeg',
  WEBP: 'webp',
} as const;

// ============= File Size Limits =============
export const FILE_SIZE_LIMITS = {
  LARGE_FILE_THRESHOLD: 30 * 1024 * 1024, // 30MB
  CHUNK_SIZE: 1000,
  CHUNK_OVERLAP: 100,
} as const;

// ============= Toast Messages =============
export const TOAST_MESSAGES = {
  ERROR_TITLE: 'Error',
  SUCCESS_TITLE: 'Success',
  WARNING_TITLE: 'Warning',
  INFO_TITLE: 'Info',
} as const;

// ============= Route Paths =============
export const ROUTES = {
  HOME: '/',
  ADMIN_DOCUMENTS: '/admin/documents',
  ADMIN_USERS: '/admin/users',
  TEAMS: '/teams',
  DOWNLOAD: '/download',
} as const;
